import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import {
  Sheet,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

interface MobileFilterSheetProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  title?: string;
  children: React.ReactNode;
}

/**
 * Full-height mobile filter drawer that leaves the AppStrip rail visible.
 *
 * The stock SheetContent spans the whole viewport (left: 0, 100vw), but the
 * AppStrip rail paints above it at z-[60] — so the drawer's first 3.5rem,
 * including its section labels, rendered underneath the rail. Overlay and
 * panel are offset by the rail width instead, mirroring the mobile nav sheet
 * in ui/sidebar.tsx, and taps on the rail don't dismiss the drawer.
 */
export const MobileFilterSheet: React.FC<MobileFilterSheetProps> = ({
  isOpen,
  onOpenChange,
  title = "Filters",
  children,
}) => (
  <Sheet onOpenChange={onOpenChange} open={isOpen}>
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        style={{ left: "3.5rem" }}
      />
      <DialogPrimitive.Content
        className="fixed inset-y-0 z-50 flex h-full flex-col border-r border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left"
        onPointerDownOutside={(e) => {
          // Don't close the drawer when tapping the AppStrip rail.
          const target = e.target as HTMLElement;
          if (target.closest("[data-appstrip]")) {
            e.preventDefault();
          }
        }}
        style={{ left: "3.5rem", width: "calc(100vw - 3.5rem)" }}
      >
        <SheetHeader className="flex-shrink-0 px-6 pt-6">
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription className="sr-only">
            Narrow the list with the filters below.
          </SheetDescription>
        </SheetHeader>
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm text-gray-500 dark:text-gray-400 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
        <div className="flex-1 overflow-y-auto px-6 pb-6">{children}</div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  </Sheet>
);

export default MobileFilterSheet;
